/**
 * 灵根系统
 * 五行元素 + 四档品质，影响修炼速度和突破难度
 * 核心逻辑：元素越少修炼越快，品质越高加成越大
 */

/** 五行元素 */
export type Element = '金' | '木' | '水' | '火' | '土';

/** 灵根品质 */
export type RootGrade = '天' | '地' | '玄' | '黄';

const ALL_ELEMENTS: Element[] = ['金', '木', '水', '火', '土'];

/** 品质配置 */
export interface GradeConfig {
  grade: RootGrade;
  name: string;
  /** 元素数量范围 [min, max] */
  elementRange: [number, number];
  /** 品质权重系数（用于速度公式） */
  gradeWeight: number;
  /** 突破成功率基础加成 */
  breakthroughBase: number;
  /** 随机权重 */
  weight: number;
}

export const GRADE_CONFIGS: GradeConfig[] = [
  { grade: '天', name: '天灵根', elementRange: [1, 1], gradeWeight: 1.0,  breakthroughBase: 0.25, weight: 3 },
  { grade: '地', name: '地灵根', elementRange: [1, 2], gradeWeight: 0.85, breakthroughBase: 0.18, weight: 12 },
  { grade: '玄', name: '玄灵根', elementRange: [2, 3], gradeWeight: 0.70, breakthroughBase: 0.08, weight: 35 },
  { grade: '黄', name: '黄灵根', elementRange: [3, 5], gradeWeight: 0.55, breakthroughBase: 0.0,  weight: 50 },
];

/** 玩家灵根 */
export interface SpiritualRoot {
  grade: RootGrade;
  elements: Element[];
  /** 修炼速度倍率 */
  speedMultiplier: number;
  /** 突破成功率加成 */
  breakthroughBonus: number;
}

/**
 * 计算修炼速度倍率
 * 公式：2.0 / sqrt(元素数) × 品质权重
 * 元素越少越快，品质越高越快
 */
function calcSpeedMultiplier(elementCount: number, gradeWeight: number): number {
  return Math.round((2.0 / Math.sqrt(elementCount)) * gradeWeight * 100) / 100;
}

/**
 * 计算突破加成
 * 公式：品质基础加成 - (元素数-1) × 0.03
 * 元素越少、品质越高，突破越容易
 */
function calcBreakthroughBonus(elementCount: number, breakthroughBase: number): number {
  return Math.round((breakthroughBase - (elementCount - 1) * 0.03) * 100) / 100;
}

/**
 * 随机生成灵根
 */
export function generateSpiritualRoot(): SpiritualRoot {
  const totalWeight = GRADE_CONFIGS.reduce((sum, g) => sum + g.weight, 0);
  let roll = Math.random() * totalWeight;
  let config!: GradeConfig;
  for (const g of GRADE_CONFIGS) {
    roll -= g.weight;
    if (roll <= 0) { config = g; break; }
  }
  if (!config) config = GRADE_CONFIGS[GRADE_CONFIGS.length - 1];

  const [min, max] = config.elementRange;
  const count = min + Math.floor(Math.random() * (max - min + 1));
  const shuffled = [...ALL_ELEMENTS].sort(() => Math.random() - 0.5);
  const elements = shuffled.slice(0, count);

  return {
    grade: config.grade,
    elements,
    speedMultiplier: calcSpeedMultiplier(count, config.gradeWeight),
    breakthroughBonus: calcBreakthroughBonus(count, config.breakthroughBase),
  };
}

/** 获取灵根显示名称 */
export function getRootDisplayName(root: SpiritualRoot): string {
  const elementsStr = root.elements.join('');
  return `${root.grade}${elementsStr}灵根`;
}

/** 获取灵根详细描述 */
export function getRootDescription(root: SpiritualRoot): string {
  const gradeDesc: Record<RootGrade, string> = {
    '天': '天道眷顾，万中无一',
    '地': '地脉滋养，天赋异禀',
    '玄': '玄门正宗，中上之资',
    '黄': '黄阶起步，勤能补拙',
  };
  const speedStr = root.speedMultiplier.toFixed(2);
  const bonusStr = root.breakthroughBonus >= 0
    ? `+${Math.round(root.breakthroughBonus * 100)}%`
    : `${Math.round(root.breakthroughBonus * 100)}%`;
  return `${gradeDesc[root.grade]}。${root.elements.join('、')}${root.elements.length}灵根，修炼速度×${speedStr}，突破${bonusStr}`;
}
